// Нова ниша › steps 5–6: save as draft, approve, and (only if asked) run discovery now.
//
// Three separate clicks on purpose. A draft costs nothing; approval is what the nightly spends
// quota on; "run now" is the only thing that spends it today. Each one shows its own error.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { QueryCandidate } from '@leadg/shared';
import { useDispatchRun } from '../../pipeline/usePipeline.js';
import type { VerticalRow } from '../verticals/useVerticals.js';
import type { CityRow } from '../cities/useCities.js';
import { toQueryRows } from './matrix.js';
import { useApproveBatch, useSaveDraftBatch } from './useWizard.js';

function ErrorLine({ error }: { error: unknown }) {
  return error ? <p className="adm__err">{(error as Error).message}</p> : null;
}

function label(pending: boolean, done: boolean, texts: [string, string, string]): string {
  if (pending) return texts[0];
  return done ? texts[2] : texts[1];
}

/** Step 6: approve the batch, optionally run tonight's job now, or go look at the rows. */
function DoneActions({
  batchId,
  vertical,
  city,
  approved,
  onApproved,
}: {
  batchId: string;
  vertical: VerticalRow;
  city: CityRow;
  approved: boolean;
  onApproved: () => void;
}) {
  const approve = useApproveBatch();
  const run = useDispatchRun();
  const runText = run.isSuccess ? `Пуснато (#${run.data})` : 'Пусни откриване сега';

  return (
    <>
      <h4 className="adm__h">6. Готово — какво следва</h4>
      <p className="adm__note">
        Черновите не се пускат. Одобрението ги активира за нощното откриване (01:00 UTC); „Пусни
        откриване сега“ ги пуска веднага и харчи квота днес.
      </p>
      <div className="adm__actions">
        <button
          type="button"
          className="adm__resolve-btn"
          disabled={approved || approve.isPending}
          onClick={() =>
            approve.mutate(
              {
                batchId,
                verticalKey: vertical.key,
                cityKey: city.key,
                activateVertical: !vertical.active,
                activateCity: !city.active,
              },
              { onSuccess: onApproved },
            )
          }
        >
          {label(approve.isPending, approved, [
            'Одобрява…',
            'Одобри и активирай',
            'Одобрено и активирано',
          ])}
        </button>
        <button
          type="button"
          className="adm__json-btn"
          disabled={!approved || run.isPending || run.isSuccess}
          onClick={() => run.mutate({ stages: ['discovery', 'details'] })}
        >
          {run.isPending ? 'Пуска…' : runText}
        </button>
        <Link className="adm__json-btn" to={`/admin?section=queries&batch=${batchId}`}>
          Виж партидата в „Заявки“
        </Link>
      </div>
      <ErrorLine error={approve.error} />
      <ErrorLine error={run.error} />
    </>
  );
}

export function StepSave({
  selected,
  vertical,
  city,
  otherActive,
}: {
  selected: QueryCandidate[];
  vertical: VerticalRow;
  city: CityRow;
  otherActive: string[];
}) {
  const [batchId] = useState(() => crypto.randomUUID());
  const [phase, setPhase] = useState<'draft' | 'saved' | 'approved'>('draft');
  const save = useSaveDraftBatch();
  const saved = phase !== 'draft';

  return (
    <div className="adm__wizard-step">
      <h4 className="adm__h">5. Запис</h4>
      <ul className="adm__summary">
        <li>
          Ниша <strong>{vertical.name_bg}</strong>
          {vertical.active ? ' (вече активна)' : ' — ще стане активна при одобрение'}
        </li>
        <li>
          Град <strong>{city.name_bg}</strong>
          {city.active ? ' (вече активен)' : ' — ще стане активен при одобрение'}
        </li>
        <li>
          <strong>{selected.length}</strong> заявки като чернова · партида{' '}
          <code>{batchId.slice(0, 8)}</code>
        </li>
      </ul>
      {otherActive.length > 0 && !vertical.active ? (
        <p className="adm__warn">
          Вече активни: {otherActive.join(', ')}. Дневният бюджет на Places се дели между нишите
          според „дял от дневния лимит“.
        </p>
      ) : null}

      <div className="adm__actions">
        <button
          type="button"
          className="adm__resolve-btn"
          disabled={saved || selected.length === 0 || save.isPending}
          onClick={() =>
            save.mutate(toQueryRows(selected, batchId, vertical.key, city.key), {
              onSuccess: () => setPhase('saved'),
            })
          }
        >
          {label(save.isPending, saved, [
            'Записва…',
            'Запази като чернова',
            'Записано като чернова',
          ])}
        </button>
        <ErrorLine error={save.error} />
      </div>

      {saved ? (
        <DoneActions
          batchId={batchId}
          vertical={vertical}
          city={city}
          approved={phase === 'approved'}
          onApproved={() => setPhase('approved')}
        />
      ) : null}
    </div>
  );
}
